import React, { useEffect, useRef, useContext, useState } from 'react';
import { GeneralContext } from '../../shared/contexts/GeneralContext.context';
import { Loading } from '../../components';
import * as api from '../../services';
import { getCookie } from '../../shared/helpers/cookies';

let widgetScriptPromise;

function loadScript(src) {
  if (customElements.get('teladoc-portal-widget')) {
    return Promise.resolve();
  }

  if (!widgetScriptPromise) {
    widgetScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(script);
    });
  }

  return widgetScriptPromise;
}

function applyWidgetAttributes(widget) {
  widget.setAttribute(
    'publishable-key',
    import.meta.env.VITE_TELADOC_PUBLISHABLE_KEY
  );
  widget.setAttribute('locale', 'en-UK');
  widget.setAttribute('embed-mode', 'false');
  widget.setAttribute('hide-header', 'true');
  widget.setAttribute('hide-footer', 'true');
}

function Consultation() {
  const widgetContainerRef = useRef(null);
  const widgetRef = useRef(null);
  const { userData } = useContext(GeneralContext);
  const [isLoading, setIsLoading] = useState(true);

  const searchParams = new URLSearchParams(window.location.search);

  const patientType = searchParams.get('patient') || 'CL';
  const dependentId = searchParams.get('clientUuid') || '';

  const consultationPatientGuid =
    patientType === 'AB'
      ? getCookie('amosAdultClientId') || ''
      : patientType === 'FM'
        ? dependentId
        : getCookie('amosClient') || '';

  useEffect(() => {
    if (userData?.isRegisteredAmos !== true) {
      return;
    }

    let cancelled = false;

    async function startWidget() {
      setIsLoading(true);
      await loadScript(import.meta.env.VITE_TELADOC_WIDGET_URL);

      if (cancelled) return;

      const widget = document.createElement('teladoc-portal-widget');

      applyWidgetAttributes(widget);
      widget.id = 'tpw';
      widgetContainerRef.current?.appendChild(widget);
      widgetRef.current = widget;

      if (!widget || typeof widget.init !== 'function') {
        throw new Error('Teladoc widget was not registered correctly');
      }

      const response =
        patientType === 'AB'
          ? await api.apiService.amos.getAdultAuthCode()
          : await api.apiService.amos.getAuthCode();

      if (cancelled) return;

      widget.init({
        authCode: response.data.auth_code,
        initialPage: {
          page: 'omc:new',
          params: {
            specialty: 'general',
            consultation_patient_guid: consultationPatientGuid,
            consultation_patient_type: 'dependent',
          },
        },
      });

      setIsLoading(false);
    }

    startWidget().catch((error) => {
      console.error('[Consultation] Widget initialization failed:', error);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
      widgetRef.current?.remove();
      widgetRef.current = null;
    };
  }, [userData?.isRegisteredAmos, patientType]);

  return (
    <>
      <Loading active={isLoading} />
      <div ref={widgetContainerRef} />
    </>
  );
}

export default Consultation;
